import { fromJson } from '@bufbuild/protobuf'
import {
  AgentQuestionKind,
  AgentQuestionSchema,
} from '../gen/service/creativefabrica/studioagent/v1/types_pb'
import type {
  AgentQuestion,
  AgentQuestionJson,
} from '../gen/service/creativefabrica/studioagent/v1/types_pb'
import type {
  ChatAgentQuestion,
  ChatAgentQuestionKind,
  ChatAgentQuestionOption,
  ChatMessage,
} from '../types'

const MODEL_CHIP_CAP = 6

function kindFrom(kind: AgentQuestionKind): ChatAgentQuestionKind | undefined {
  switch (kind) {
    case AgentQuestionKind.INTENT: {
      return 'intent'
    }
    case AgentQuestionKind.MODEL: {
      return 'model'
    }
    case AgentQuestionKind.UNSPECIFIED: {
      return 'generic'
    }
    default: {
      return undefined
    }
  }
}

export function questionFrom(question: AgentQuestion | undefined): ChatAgentQuestion | undefined {
  if (!question) {
    return undefined
  }
  const kind = kindFrom(question.kind)
  if (!kind) {
    return undefined
  }
  const options = question.options.flatMap(option => {
    const label = option.label.trim()
    if (!label) {
      return []
    }
    const description = option.description.trim()
    const iconUrl = option.iconUrl.trim()
    return [
      {
        label,
        suggested: option.suggested,
        ...(description ? { description } : {}),
        ...(iconUrl ? { iconUrl } : {}),
      },
    ]
  })
  if (options.length === 0) {
    return undefined
  }
  return { kind, options }
}

export function agentQuestionFromJson(
  json: AgentQuestionJson | undefined,
): AgentQuestion | undefined {
  if (!json) {
    return undefined
  }
  return fromJson(AgentQuestionSchema, json)
}

export function unansweredQuestionMessageId(messages: readonly ChatMessage[]): string | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]
    if (message.role === 'user') {
      return undefined
    }
    if (message.isStreaming || message.failed) {
      continue
    }
    if (message.question) {
      return message.id
    }
    return undefined
  }
  return undefined
}

export function questionChoices(question: ChatAgentQuestion): {
  chips: ChatAgentQuestionOption[]
  more: ChatAgentQuestionOption[]
} {
  if (question.kind !== 'model') {
    return { chips: question.options, more: [] }
  }
  const chips = question.options.filter(option => option.suggested)
  if (chips.length > 0) {
    return { chips, more: question.options.filter(option => !option.suggested) }
  }
  if (question.options.length <= MODEL_CHIP_CAP) {
    return { chips: question.options, more: [] }
  }
  return {
    chips: question.options.slice(0, MODEL_CHIP_CAP),
    more: question.options.slice(MODEL_CHIP_CAP),
  }
}
